import { config } from "../../../app-confi/app-config";
import { ServiceManager } from "../../../services/service-manager";
import { alignment } from "../models/alignment.enum";
import { buildingType } from "../models/building-type.enum";
import { ColumnDefinition } from "../models/ColumnDefinition/column-definition";
import { columnDefs, columnSubtype, subtypeEnum, variantEnum } from "./models/column-definition";
import { columnType } from "./models/column-type.enum";
import { columnBays } from "./models/column-types.model";
import { headType } from "./models/head-type.enum";
import { getHeadTypesModel } from "./models/head-types.model";
import { moosPlazaTypesModel } from "./models/moos-plaza-types.model";
import { PropsCorridor } from "./models/props-config";
import { segmentType } from "./models/segment-type";

export class CorridorService {

    static getColumnBays(type: columnType) {
        if (type !== columnType.none) {
            const findMoosPlazaType = columnDefs.find(s => s.type === type);
            if (findMoosPlazaType) {
                return findMoosPlazaType.bays;
            } else {
                return 0;
            }
        } else {
            return 0;
        }
    }

    static getTotalFloors(p: PropsCorridor) {
        return Math.floor(p.buildingHeight / config.corridordFloorHeight);
    }

    static getTotalPercentages(p: PropsCorridor) {
        const cTypePercentages = p.unitWishList.map(s => s.percentage);
        return cTypePercentages.reduce((a, b) => a + b, 0);
    }

    static getExtraElevators(p: PropsCorridor) {
        let minElevatorsNeededBasedOnHeight = 0;
        let minElevatorsNeededBasedOnWidth = 0;

        // const bodies = this.getBodyNumber();
        // const avgBodySizeBays = this.getBodySizeForCalculation() / bodies;
        // const maxWalkingDistance = avgBodySizeBays / 2 * 3;
        const maxNumberOfUnits = Math.floor(this.getBodySizeWithoutMoosPleinAndHeads(p) / 3) * 2 * CorridorService.getTotalFloors(p);
        minElevatorsNeededBasedOnWidth = Math.ceil(maxNumberOfUnits / 100);

        const floors = this.getTotalFloorsForElevator(p);
        if (floors > 4 && floors <= 9) {
            minElevatorsNeededBasedOnHeight = 1;
        } else if (floors > 9) {
            minElevatorsNeededBasedOnHeight = 2;
        }

        let res = 0;
        if (floors > 4) {
            res = minElevatorsNeededBasedOnHeight > minElevatorsNeededBasedOnWidth ? minElevatorsNeededBasedOnHeight : minElevatorsNeededBasedOnWidth;
        } else {
            res = minElevatorsNeededBasedOnHeight;
        }
        return res;
    }

    static getTotalBays(p: PropsCorridor) {
        return Math.ceil(p.buildingLength / config.corridorBaySize);
    }

    static getBodySizeWithoutMoosPleinAndHeads(p: PropsCorridor) {
        let bodySize = CorridorService.getTotalBays(p) - CorridorService.getMoosPlazaBays(p.moosPlazaType);
        if (p.selectedHeadType !== headType.none) {
            bodySize -= this.getHeadTypeBays(p.selectedHeadType) * 2;
        }

        return bodySize > 0 ? Math.ceil(bodySize) : 0;
    }

    static getStaircaseCores(p: PropsCorridor) {
        const extraCores = CorridorService.getExtraCoresBasedOnColumnAmounts(p);
        const coresToOccupyWithElevators = CorridorService.getCoresToOccupyWithElevators(p);
        const staircases = extraCores - coresToOccupyWithElevators;
        return staircases > 0 ? staircases : 0;
    }

    static getBodySizeForCalculation(p: PropsCorridor) {
        let bodySize = this.getBodySizeWithoutMoosPleinAndHeads(p);

        // how many staircases / elevators we need to add determines this final body count as well for columns
        const staircaseCores = this.getStaircaseCores(p);
        const coresToOccupyWithElevators = this.getCoresToOccupyWithElevators(p);

        bodySize -= staircaseCores;
        bodySize -= coresToOccupyWithElevators * 2;

        return bodySize > 0 ? bodySize : 0;
    }

    static getBodySizeForComputationBasedOnColumnsAmount(p: PropsCorridor) {
        return p.amount_C_XS_Columns * columnBays.find(c => c.type === columnType.C_XS).bays +
            p.amount_C_S_Columns * columnBays.find(c => c.type === columnType.C_S1S2).bays +
            p.amount_C_M_Columns * columnBays.find(c => c.type === columnType.C_M).bays +
            p.amount_C_L_Columns * columnBays.find(c => c.type === columnType.C_L).bays +
            p.amount_C_XL_Columns * columnBays.find(c => c.type === columnType.C_XL).bays;
    }

    static getExtraCoresBasedOnColumnAmounts(p: PropsCorridor) {
        const bodySize = this.getBodySizeForComputationBasedOnColumnsAmount(p);
        let extraCores = Math.floor(bodySize * config.corridorBaySize / config.maxBodyLengthCorridor)

        if (bodySize !== 0 && p.moosPlazaAlignment === alignment.center) {
            extraCores += 2;
        } else if (bodySize !== 0 && p.moosPlazaAlignment !== alignment.center) {
            extraCores += 1;
        }

        return extraCores;
    }

    static getCoresToOccupyWithElevators(p: PropsCorridor) {
        const extraElevators = this.getExtraElevators(p);
        const elevatorsProvidedByMoosPlaza = 2;
        const elevatorsProvidedByHeads = 0;

        // how many cores we need to give to elevators OTLH2 types?
        const elevatorsProvidedByMoosPlazaAndHeads = elevatorsProvidedByMoosPlaza + elevatorsProvidedByHeads;
        if (elevatorsProvidedByMoosPlazaAndHeads < extraElevators) {
            const elevatorsStillNeededInCores = extraElevators - elevatorsProvidedByMoosPlazaAndHeads;

            // here we have to reflect on type of core and divide by 2 if 2 elevators fit the core
            return Math.ceil(elevatorsStillNeededInCores);
        } else {
            return 0;
        }
    }

    static getMoosPlazaBays(type: columnType) {
        const findMoosPlazaType = moosPlazaTypesModel.find(s => s.type === type);
        return findMoosPlazaType.bays;
    }

    static getHeadTypeBays(type: headType) {
        const findHeadType = getHeadTypesModel().find(s => s.type === type);
        return findHeadType ? findHeadType.bays : 0;
    }

    static getTotalFloorsForElevator(p: PropsCorridor) {
        // crown is always treated as 1 floor when determining the need of elevators
        return p.plinthFloors + ServiceManager.buildingHelperService.getMiddleFloors(p.plinthType, p.buildingHeight, p.customPlinthHeight, p.crownFloors, p.plinthFloors, buildingType.doubleLoadedCorridor) + 1;
    }


    static getAmountsByAvailableBays(p: PropsCorridor): {
        amount_C_XS_Columns: number;
        amount_C_S1S2_Columns: number;
        amount_C_M_Columns: number;
        amount_C_L_Columns: number;
        amount_C_XL_Columns: number;
    } {
        const bays_C_XS = columnBays.find(c => c.type === columnType.C_XS).bays;
        const bays_C_S = columnBays.find(c => c.type === columnType.C_S1S2).bays;
        const bays_C_M = columnBays.find(c => c.type === columnType.C_M).bays;
        const bays_C_L = columnBays.find(c => c.type === columnType.C_L).bays;
        const bays_C_XL = columnBays.find(c => c.type === columnType.C_XL).bays;

        let availableBays = CorridorService.getBodySizeForCalculation(p);

        let amount_C_XS = 0;
        let amount_C_M = 0;
        let amount_C_S = 0;
        let amount_C_L = 0;
        let amount_C_XL = 0;

        let result = {} as any;

        if (availableBays > 0) {
            const removeBays = () => {
                availableBays -= bays_C_XS;
                if (availableBays > 0) {
                    amount_C_XS++;
                    availableBays -= bays_C_S;
                    if (availableBays > 0) {
                        amount_C_S++;
                        availableBays -= bays_C_XL;
                        if (availableBays > 0) {
                            amount_C_XL++;
                            availableBays -= bays_C_M;
                            if (availableBays > 0) {
                                amount_C_M++;
                                availableBays -= bays_C_L;
                                if (availableBays > 0) {
                                    amount_C_L++;
                                    removeBays();
                                }
                            }
                        }
                    }
                }
            }
            removeBays();
            result.amount_C_XS_Columns = amount_C_XS;
            result.amount_C_S1S2_Columns = amount_C_S;
            result.amount_C_M_Columns = amount_C_M;
            result.amount_C_L_Columns = amount_C_L;
            result.amount_C_XL_Columns = amount_C_XL;
        } else {
            result.amount_C_XS_Columns = 0;
            result.amount_C_S1S2_Columns = 0;
            result.amount_C_M_Columns = 0;
            result.amount_C_L_Columns = 0;
            result.amount_C_XL_Columns = 0;
        }

        return result;
    }

}